/**
 * Página de edição de recursos.
 *
 * Este componente renderiza um formulário para editar recursos existentes,
 * permitindo modificar categoria, tipo, nome e descrição do recurso.
 *
 * @module EditResources
 */

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Button from '../../components/Button/Button';
import ContainerPage from '../../components/ContainerPage/ContainerPage';
import TextEditor from '../../components/Resources/TextEditor/TextEditor';
import useEditResource from '../../hooks/useEditResource';
import { AlertContainer, Back, FormStyle, Main } from './EditResources.Styles';

/**
 * Componente funcional que renderiza o formulário de edição de recursos.
 *
 * Utiliza o hook personalizado useEditResource para gerenciar o estado
 * e as operações relacionadas à edição de recursos.
 *
 * @returns {JSX.Element} Componente de edição de recursos
 */
const EditResources = () => {
  // Hook de tradução para internacionalização
  const { t } = useTranslation();

  // Estados e funções do hook personalizado useEditResource
  const {
    editorContent,
    setEditorContent,
    categories,
    category,
    setCategory,
    type,
    setType,
    name,
    setName,
    errorMsg,
    successMsg,
    errors,
    loading,
    editResource,
  } = useEditResource();

  return (
    <ContainerPage>
      {/* Cabeçalho com botão de voltar */}
      <Back>
        <button>
          <Link to={'/Resources'}>
            <ArrowBackIosNewIcon htmlColor="black" fontSize="medium" />
          </Link>
        </button>
        <span>{t('Edit Resource')}</span>
      </Back>

      {/* Conteúdo principal */}
      <Main>
        <FormStyle>
          {/* Exibe indicador de carregamento ou formulário */}
          {loading ? (
            <div className="loading">
              <CircularProgress size={18} />
            </div>
          ) : (
            <form onSubmit={editResource}>
              {/* Seletor de categoria */}
              <select
                name="category"
                className={errors.category ? 'error' : ''}
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" disabled hidden>
                  {t('Category')}
                </option>
                {categories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>

              {/* Campo para tipo de recurso */}
              <input
                type="text"
                id="type"
                name="type"
                className={errors.type ? 'error' : ''}
                placeholder={t('Digite o tipo de recurso')}
                value={type}
                onChange={(e) => setType(e.target.value)}
              />

              {/* Campo para nome do recurso */}
              <input
                type="text"
                id="name"
                name="name-resource"
                className={errors.name ? 'error' : ''}
                placeholder={t('Resource Name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              {/* Editor de texto para descrição do recurso */}
              <div className="insert-text">
                <TextEditor
                  className={errors.editor ? 'error' : ''}
                  value={editorContent}
                  onChange={setEditorContent}
                />
              </div>

              {/* Botão de salvar */}
              <div className="button-save">
                <Button text={t('Save')} variant="lg" />
              </div>
            </form>
          )}
        </FormStyle>
      </Main>

      {/* Alertas de erro ou sucesso */}
      {(errorMsg || successMsg) && (
        <AlertContainer>
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          {successMsg && <Alert severity="success">{successMsg}</Alert>}
        </AlertContainer>
      )}
    </ContainerPage>
  );
};

export default EditResources;
